// Copyright (c) 2019 Celadon Development LLC, All rights reserved.
// Author Yana Sidorova <yana.sidorova@celadon.ae>
import {
  put, call, takeEvery, select
} from 'redux-saga/effects';
import * as firebase from 'firebase/app';
import ReactPixel from 'react-facebook-pixel';

// eslint-disable-next-line max-len
import { profileService } from '../../account/components/AccountTab/components/MyProfile/store/service';
import { getActiveDiscountCode } from '../../get-paid/store/selectors';
import { confirmOrderService, confirmOrderForCharityService, getCharitiesService } from './service';
import { basketService } from '../../basket/store/service';
import * as basketActions from '../../basket/store/actions';
import * as confirmOrderActions from './actions';
import * as types from './actionTypes';

function* confirmOrderSaga(payload) {
  const basket = yield call(basketService, { CartID: payload.CartID });
  const result = yield call(confirmOrderService, payload);
  if (result.ok) {
    const response = yield call(profileService, { id: payload.CustomerId, token: payload.Token });

    firebase.analytics().logEvent('order_confirm', {
      platform: 'Desktop',
    });

    const total = JSON.parse(localStorage.getItem('total'));
    ReactPixel.trackCustom('order_confirm', total);

    if (response.ok) {
      const profile = response.body;
      const activeDiscountCode = yield select(getActiveDiscountCode);

      const totalCost = basket.body.CartDetails.reduce((acc, item) => acc + item.SellbackPrice, 0);
      const items = basket.body.CartDetails.map((item) => {
        return {
          item_id: item.EAN,
          item_name: item.Title,
          currency: 'GBP',
          price: item.SellbackPrice,
          quantity: 1
        };
      });

      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'purchase',
        customer_email: `${profile?.EmailID}` ?? '',
        customer_phone: `${profile?.Mobile}` ?? '', // make sure to add the country code
        user_id: `${profile?.CustomerId}` ?? '',
        ecommerce: {
          transaction_id: `${(JSON.parse(result?.body)).OrderID}`, // Order id
          value: totalCost,
          currency: 'GBP',
          coupon: activeDiscountCode?.Code ?? '',
          items,
        }
      });
    }

    localStorage.removeItem('CartID');
    yield put(basketActions.basketReset());
    localStorage.setItem('wasQualified', 0);
  } else {
    const message = result.body.Reason || 'Some error';
    yield put(confirmOrderActions.requestConfirmFailed({
      message,
      status: false,
    }));
  }
}

function* confirmOrderForCharitySaga(payload) {
  const basket = yield call(basketService, { CartID: payload.CartID });
  const result = yield call(confirmOrderForCharityService, payload);

  if (result.ok) {
    const response = yield call(profileService, { id: payload.CustomerId, token: payload.Token });

    firebase.analytics().logEvent('order_confirm_for_charity', {
      platform: 'Desktop',
    });

    if (response.ok) {
      const profile = response.body;
      const activeDiscountCode = yield select(getActiveDiscountCode);

      const totalCost = basket.body.CartDetails.reduce((acc, item) => acc + item.SellbackPrice, 0);
      const items = basket.body.CartDetails.map((item) => {
        return {
          item_id: item.EAN,
          item_name: item.Title,
          currency: 'GBP',
          price: item.SellbackPrice,
          quantity: 1
        };
      });

      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'purchase',
        customer_email: `${profile?.EmailID}` ?? '',
        customer_phone: `${profile?.Mobile}` ?? '', // make sure to add the country code
        user_id: `${profile?.CustomerId}` ?? '',
        ecommerce: {
          transaction_id: `${(JSON.parse(result?.body)).OrderID}`, // Order id
          value: totalCost,
          currency: 'GBP',
          coupon: activeDiscountCode?.Code ?? '',
          items,
        }
      });
    }

    localStorage.removeItem('CartID');
    yield put(basketActions.basketReset());
    localStorage.setItem('wasQualified', 0);
  } else {
    const message = result.body.Reason || 'Some error';
    yield put(confirmOrderActions.requestConfirmFailed({
      message,
      status: false,
    }));
  }
}

function* getCharitiesSaga() {
  const res = yield call(getCharitiesService);

  if (res.ok) {
    firebase.analytics().logEvent('get_charities', {
      platform: 'Desktop',
    });
    yield put(confirmOrderActions.getCharitiesSuccess(res.body));
  }
}

const confirmOrderSagas = [
  takeEvery(types.CONFIRM_REQUEST, confirmOrderSaga),
  takeEvery(types.CONFIRM_ORDER_FOR_CHARITY, confirmOrderForCharitySaga),
  takeEvery(types.GET_CHARITIES, getCharitiesSaga),
];

export default confirmOrderSagas;
